import { ThunderboltFilled } from "@ant-design/icons";
import type { InputRef } from "antd";
import {
	Alert,
	Button,
	Card,
	ConfigProvider,
	Divider,
	Flex,
	Form,
	Input,
	Layout,
	QRCode,
	Spin,
	Steps,
	Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { silentInstance } from "../api-client.ts";
import type { RootState } from "../store.ts";
import { loginTheme } from "../theme";
import styles from "./OnboardingPage.module.css";

type Props = {
	secret: string | undefined;
};

type PageStatus = "loading" | "invalid_link" | "ready";

function buildOtpAuthUri(key: string, email: string): string {
	return `otpauth://totp/${encodeURIComponent(email)}?${new URLSearchParams({
		secret: key,
		issuer: location.hostname,
	}).toString()}`;
}

const stepItems = [
	{ title: "Scan QR Code" },
	{ title: "Verify Code" },
	{ title: "Set Password" },
];

const OnboardingPage: React.FC<Props> = ({ secret, email }) => {
	const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
	const [currentStep, setCurrentStep] = useState(0);
	const [otpKey, setOtpKey] = useState("");
	const [stepError, setStepError] = useState<string | null>(null);
	const [stepLoading, setStepLoading] = useState(false);
	const [step2Form] = Form.useForm<{ token: string }>();
	const [step3Form] = Form.useForm<{
		password: string;
		confirmPassword: string;
	}>();
	const totpInputRef = useRef<InputRef>(null);

	useEffect(() => {
		if (!secret) {
			setPageStatus("invalid_link");
			return;
		}
		silentInstance
			.get("/api/v1/management/login/get_mfa_key/", { params: { secret } })
			.then((response) => {
				setOtpKey(response.data.key);
				setPageStatus("ready");
			})
			.catch(() => {
				setPageStatus("invalid_link");
			});
	}, [secret]);

	const handleStep2Submit = async (values: { token: string }) => {
		setStepLoading(true);
		setStepError(null);
		try {
			await silentInstance.post(
				"/api/v1/management/login/confirm_mfa_key/",
				{ token: values.token },
				{ params: { secret } },
			);
			setCurrentStep(2);
		} catch (err: unknown) {
			const axiosErr = err as {
				response?: { data?: { error?: string; detail?: string } };
			};
			setStepError(
				axiosErr?.response?.data?.error ??
					axiosErr?.response?.data?.detail ??
					"Invalid code. Please check your authenticator app and try again.",
			);
			step2Form.resetFields();
			setTimeout(() => totpInputRef.current?.focus(), 0);
		} finally {
			setStepLoading(false);
		}
	};

	const handleStep3Submit = async (values: { password: string }) => {
		setStepLoading(true);
		setStepError(null);
		try {
			await silentInstance.post(
				"/api/v1/management/login/set_password/",
				{ password: values.password },
				{ params: { secret } },
			);
			window.location.replace("/login");
		} catch (err: unknown) {
			const axiosErr = err as {
				response?: { data?: { error?: string; detail?: string } };
			};
			setStepError(
				axiosErr?.response?.data?.error ??
					axiosErr?.response?.data?.detail ??
					"Failed to set password. Please try again.",
			);
		} finally {
			setStepLoading(false);
		}
	};

	const renderCardContent = () => {
		if (pageStatus === "loading") {
			return (
				<Flex justify="center" className={styles.loadingContainer}>
					<Spin size="large" />
				</Flex>
			);
		}

		if (pageStatus === "invalid_link") {
			return (
				<Alert
					type="error"
					title="Invalid Setup Link"
					description="This setup link is invalid or has expired. Please contact your administrator."
					showIcon
				/>
			);
		}

		return (
			<>
				<Steps
					current={currentStep}
					items={stepItems}
					className={styles.steps}
					size="small"
				/>

				{currentStep === 0 && (
					<Flex vertical align="center" gap={16}>
						<Typography.Text className={styles.instruction}>
							Scan this QR code with your authenticator app (Google
							Authenticator, Authy, etc.)
						</Typography.Text>
						<QRCode value={buildOtpAuthUri(otpKey, email)} size={180} />
						<Flex vertical align="center" gap={4}>
							<Typography.Text className={styles.manualCodeLabel}>
								Manual entry code
							</Typography.Text>
							<Typography.Text className={styles.manualCode} copyable>
								{otpKey}
							</Typography.Text>
						</Flex>
						<Button
							type="primary"
							size="large"
							block
							onClick={() => setCurrentStep(1)}
							style={{ fontWeight: 600, marginTop: 8 }}
						>
							I&apos;ve scanned the code
						</Button>
					</Flex>
				)}

				{currentStep === 1 && (
					<Form
						form={step2Form}
						layout="vertical"
						onFinish={handleStep2Submit}
						autoComplete="off"
						className={styles.form}
					>
						{stepError && (
							<Alert
								type="error"
								title={stepError}
								showIcon
								className={styles.errorContainer}
							/>
						)}
						<Form.Item
							name="token"
							label={
								<Typography.Text className={styles.label}>
									Authenticator Code
								</Typography.Text>
							}
							rules={[
								{ required: true, message: "Please enter your 6-digit code." },
								{ len: 6, message: "Code must be exactly 6 digits." },
								{ pattern: /^\d+$/, message: "Code must contain digits only." },
							]}
						>
							<Input
								ref={totpInputRef}
								size="large"
								maxLength={6}
								autoFocus
								placeholder="000000"
							/>
						</Form.Item>
						<Form.Item label={null} className={styles.submitFormItem}>
							<Button
								type="primary"
								htmlType="submit"
								size="large"
								block
								loading={stepLoading}
								style={{ fontWeight: 600 }}
							>
								Verify
							</Button>
						</Form.Item>
					</Form>
				)}

				{currentStep === 2 && (
					<Form
						form={step3Form}
						layout="vertical"
						onFinish={handleStep3Submit}
						autoComplete="off"
						className={styles.form}
					>
						{stepError && (
							<Alert
								type="error"
								title={stepError}
								showIcon
								className={styles.errorContainer}
							/>
						)}
						<Form.Item
							name="password"
							label={
								<Typography.Text className={styles.label}>
									New Password
								</Typography.Text>
							}
							rules={[
								{ required: true, message: "Please enter a password." },
								{ min: 8, message: "Password must be at least 8 characters." },
							]}
						>
							<Input.Password size="large" placeholder="••••••••" />
						</Form.Item>
						<Form.Item
							name="confirmPassword"
							label={
								<Typography.Text className={styles.label}>
									Confirm Password
								</Typography.Text>
							}
							dependencies={["password"]}
							rules={[
								{ required: true, message: "Please confirm your password." },
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue("password") === value) {
											return Promise.resolve();
										}
										return Promise.reject(new Error("Passwords do not match."));
									},
								}),
							]}
						>
							<Input.Password size="large" placeholder="••••••••" />
						</Form.Item>
						<Form.Item label={null} className={styles.submitFormItem}>
							<Button
								type="primary"
								htmlType="submit"
								size="large"
								block
								loading={stepLoading}
								style={{ fontWeight: 600 }}
							>
								Set Password
							</Button>
						</Form.Item>
					</Form>
				)}
			</>
		);
	};

	return (
		<ConfigProvider theme={loginTheme}>
			<Layout>
				<Content>
					<Flex
						vertical
						className={styles.pageWrapper}
						align="center"
						justify="center"
					>
						<Card className={styles.card}>
							<Flex align="center" gap={12} className={styles.brand}>
								<Flex
									align="center"
									justify="center"
									className={styles.iconWrap}
								>
									<ThunderboltFilled className={styles.brandIcon} />
								</Flex>
								<Flex vertical>
									<Typography.Text className={styles.brandName}>
										DZ Projects
									</Typography.Text>
									<Typography.Text className={styles.brandTag}>
										Wind Asset Management
									</Typography.Text>
								</Flex>
							</Flex>
							<Divider className={styles.divider} />
							{renderCardContent()}
						</Card>
					</Flex>
				</Content>
			</Layout>
		</ConfigProvider>
	);
};

export const ConnectedOnboardingPage = connect(
	(state: RootState) => state.router.route?.params,
)(OnboardingPage);

export default ConnectedOnboardingPage;
